import { useMemo } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { getAuth0Config } from "../utils/config";
import { AuthContext } from "./AuthContext";
import type { AuthContextValue, LogoutOptions } from "./types";

interface Props {
  children: JSX.Element;
}

const Auth0Bridge = ({ children }: Props) => {
  const {
    isAuthenticated,
    isLoading,
    error,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const value: AuthContextValue = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      error: error ?? null,
      user,
      login: () => loginWithRedirect(),
      logout: (options?: LogoutOptions) =>
        logout({ returnTo: options?.returnTo ?? window.location.origin }),
      getToken: () => getAccessTokenSilently(),
      isAuthenticatedFn: () => isAuthenticated,
    }),
    [
      isAuthenticated,
      isLoading,
      error,
      user,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const Auth0AuthProvider = ({ children }: Props) => {
  const config = getAuth0Config();

  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      redirectUri={window.location.origin}
      audience={config.audience}
      scope="read:current_user read:user update:current_user_metadata"
    >
      <Auth0Bridge>{children}</Auth0Bridge>
    </Auth0Provider>
  );
};

export default Auth0AuthProvider;
